import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { ACTIONS } from '../redux/actions';
import { SuccessToast, ErrorToast } from '../Utils/toast';
import SideModal from "./SideModal";
import { UploadCloud, Download, Info, ShoppingBag, Eye, X, Trash2 } from "lucide-react";
import { MdOutlineFileUpload } from "react-icons/md";
import AddBulkicon from "../assets/AddBulkicon.png";
import * as XLSX from 'xlsx';
import Loading from "../Common/Loading";


const AddAgent = ({
  onClose,
  sidebarTitle = "Add Bulk Agents",
  sampleFileName = "Sample_Inventory",
  csvFileName = "Sample_Inventory.csv",
  xlsxFileName = "Sample-Inventory.xlsx",
  sampleFileUrls = { excel: "/Sample-Inventory.xlsx", csv: "/Sample_Inventory.csv" },
  onUpload = () => { },
  onSubmit = () => { },
  BottomComponent
}) => {
  const dispatch = useDispatch();
  const bulkState = useSelector(s => s.CreateAgentBulk);
  const fileInputRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("excel"); // State for radio button selection
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);


  const handleClose = (e) => {
    setModalOpen(false);
    onClose(e);
  };

  const handleDownload = (type) => {
    const link = document.createElement("a");

    if (selectedFormat === "csv") {
      link.href = sampleFileUrls.csv;
      link.download = csvFileName;
    } else {
      link.href = sampleFileUrls.excel;
      link.download = xlsxFileName;
    }

    link.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      parseExcelFile(file);
      onUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      parseExcelFile(file);
    }
  };

  const parseExcelFile = (file) => {
    setPreviewLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        // Filter out completely empty rows
        const filteredData = jsonData.filter(row => 
          Array.isArray(row) && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')
        );
        setExcelData(filteredData);
        // setPreviewVisible(true); // Disable automatic preview
        setPreviewLoading(false);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setExcelData(null);
        alert('Error reading Excel file. Please ensure it is a valid Excel file.');
        setPreviewLoading(false);
      }
    };
    reader.onerror = () => {
      console.error('Error reading file');
      setExcelData(null);
      alert('Error reading file. Please try again.');
      setPreviewLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };


  const togglePreview = () => {
    setPreviewVisible(!previewVisible);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleSubmit = () => {
    if (!excelData || excelData.length <= 1) {
      ErrorToast('No rows found in the Excel file. Submission blocked.');
      return;
    }
    if (selectedFile) {
      const fd = new FormData();
      fd.append('file', selectedFile);
      setIsSubmitting(true);
      dispatch(ACTIONS.CreateAgentBulkAction(fd));
    }
  };

  // Handle CreateAgentBulk response
  useEffect(() => {
    if (isSubmitting) {
      if (bulkState.CreateAgentBulkLoading) {
        // Keep spinner - loading state is handled by the button
        return;
      }

      if (bulkState.CreateAgentBulkSuccess) {
        const { inserted = 0, failed = 0 } = bulkState.CreateAgentBulkData || {};
        SuccessToast(`failed: ${failed} inserted: ${inserted}`);
        // NOTE: Do NOT dispatch REFRESH_DATA here — the saga already dispatches it
        // on success. Dispatching it again causes double API calls and infinite re-renders.
        setModalOpen(false);
        onClose();
        setIsSubmitting(false);
        return;
      }

      if (bulkState.CreateAgentBulkError) {
        // Safely convert error to string — passing Error objects to toast crashes React
        const errMsg = typeof bulkState.CreateAgentBulkError === 'string'
          ? bulkState.CreateAgentBulkError
          : bulkState.CreateAgentBulkError?.message || 'Failed to create bulk agents';
        ErrorToast(errMsg);
        setIsSubmitting(false);
        return;
      }
    }
  }, [bulkState, isSubmitting, onClose]);

  return (
    <>
      <Loading loading={previewLoading} />
      <div>

        {modalOpen && (
          <SideModal
            title={sidebarTitle}
            // Uses sidebarTitle prop
            image={AddBulkicon}
            onClose={handleClose}
            setModalOpen={setModalOpen}
          >
            <div className="flex flex-col gap-5">
              {/* Format Selection Radio Buttons */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Select Sample File Format:</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="fileFormat"
                      value="excel"
                      checked={selectedFormat === "excel"}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">EXCEL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="fileFormat"
                      value="csv"
                      checked={selectedFormat === "csv"}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">CSV</span>
                  </label>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleDownload("header")}
                  className="text-blue-700 flex items-center gap-1 hover:underline"
                >
                  <Download size={16} /> Download sample file
                </button>
              </div>

              {/* File Dropzone */}
              <div>
                {/* Label would use sidebarTitle prop */}
                {!selectedFile ? (
                  <div
                    onClick={() => fileInputRef.current.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-green-600 flex p-[33.5px] rounded-md text-center justify-center items-center cursor-pointer hover:bg-green-50 transition"
                  >
                    <p className="text-green-600 font-medium">Select/Drop File here</p>
                    <MdOutlineFileUpload className="ml-3 mt-1 text-[16px] text-green-600" />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="border border-[#07AE4D] bg-green-50 p-3 rounded-md flex justify-between items-center">
                    <span className="text-[#07AE4D] text-sm">
                      Selected: <strong>{selectedFile.name}</strong> <br />({excelData ? excelData.length - 1 : 0} records found)
                    </span>
                    <div className="flex gap-2">
                      {excelData && (
                        <button
                          onClick={togglePreview}
                          className="text-green-600 text-xs hover:underline flex items-center gap-1"
                        >
                          <Eye size={12} />
                          Preview
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedFile(null);
                          setExcelData(null);
                          setPreviewVisible(false);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        className="text-red-600 text-xs hover:underline flex items-center gap-1 ml-2"
                      >
                        <Trash2 size={12} />
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {BottomComponent ? <BottomComponent onSubmit={handleSubmit} selectedFile={selectedFile} /> :
                <>
                  <div className="text-[#9C9B9B] text-sm  gap-1">
                    Steps to Add Members in bulk
                    <ul className="text-[13px] text-[#9C9B9B] list-disc ml-5 leading-relaxed mt-4">
                      <li>Download Sample File</li>
                      <li>Fill Up all the columns</li>
                      <li>Upload the file and check the status</li>
                    </ul>
                  </div>

                  {/* Submit Button */}
                  {selectedFile && (
                    <button
                      onClick={handleSubmit}
                      disabled={bulkState.CreateAgentBulkLoading}
                      className="mt-2 bg-green-600 text-white border border-green-600 rounded-full py-2 hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {bulkState.CreateAgentBulkLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  )}
                </>
              }
            </div>
          </SideModal>
        )}
      </div>

      {/* Excel Preview Modal */}
      {previewVisible && excelData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center sm:justify-start px-4">
          <div className="bg-white rounded-lg shadow-xl w-[95%] sm:w-[85%] lg:w-[70%]  xl:ml-0 2xl:ml-20 overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Excel Preview - {selectedFile?.name}</h3>
              <button
                onClick={togglePreview}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              {excelData.length > 0 ? (
                <div className="overflow-x-auto max-h-[65vh] border border-gray-300 rounded-lg">
                  <table className="min-w-[600px] w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-100">
                      <tr>
                        {excelData[0]?.map((header, index) => (
                          <th
                            key={index}
                            className="border-b border-r border-gray-300 px-4 py-2 text-left font-medium whitespace-nowrap first:border-l"
                          >
                            {header || `Column ${index + 1}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.slice(1).map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="border-b border-r border-gray-300 px-4 py-2 whitespace-nowrap text-xs first:border-l"
                            >
                              {cell || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No data found in the Excel file
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {excelData.length > 0 ? excelData.length - 1 : 0} rows
              </div>
              <button
                onClick={togglePreview}
                className="px-4 py-2 bg-red-600 text-white rounded-md transition"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddAgent;
